/* SpawnAnything — small comic voxel models for the item window's cards (src/ui/itemWindow.js): one per tool of the
   battle tool belt (hand, grab hand, explosives, crate tools, disasters) plus the boss crate. Menu only: the thrown
   grenade / barrel in the battle are built elsewhere (src/fx/thrown.js). Static models, painted by src/ui/preview3d.js
   through voxObject (toon body + outline). y is up, the model stands on y = 0; the preview centres and fits it. */
import { VoxModel } from '../game/voxel.js';
import { voxObject } from './models3d.js';

const hash = (x, y, z) => { let h = (x * 374761393 + y * 668265263 + z * 2147483647) | 0; h = (h ^ (h >>> 13)) * 1274126177; return ((h ^ (h >>> 16)) >>> 0) / 4294967296; };
/** upright cylinder (axis y) */
function cyl(m, cx, cz, r, y0, y1, c) {
  const R = Math.ceil(r);
  for (let x = -R; x <= R; x++) for (let z = -R; z <= R; z++) {
    if (x * x + z * z > r * r + r * 0.6) continue;
    for (let y = y0; y <= y1; y++) m.set(cx + x, y, cz + z, typeof c === 'function' ? c(cx + x, y, cz + z) : c);
  }
}
/** flat ring (axis y) between r0 and r1 */
function ring(m, cx, cy, cz, r0, r1, c) {
  const R = Math.ceil(r1);
  for (let x = -R; x <= R; x++) for (let z = -R; z <= R; z++) {
    const d = Math.hypot(x, z); if (d < r0 || d > r1 + 0.35) continue;
    m.set(cx + x, cy, cz + z, typeof c === 'function' ? c(cx + x, cy, cz + z) : c);
  }
}
const SKIN = 0xf2c49b, SKIN_D = 0xd9a073, GLOVE = 0xf7f7f2, GLOVE_D = 0xd8d8d0;

/** a chunky comic hand: palm + fingers (1 = pointing, 4 = open and spread), thumb, cuff */
function hand(m, { fingers, c, cd, cuff, glow = false }) {
  m.box(-5, 0, -2, 4, 3, 2, cuff);                    // cuff / sleeve
  m.box(-5, 4, -2, 4, 4, 2, cd);
  m.box(-5, 5, -2, 4, 11, 1, c);                      // palm
  if (fingers === 1) {
    m.box(-1, 12, -2, 1, 21, 0, c); m.box(-1, 21, -2, 1, 21, -2, cd); // index finger up
    m.box(-5, 10, 1, 4, 12, 3, cd);                   // curled fingers
  } else {
    for (const [x, h] of [[-5, 4], [-2, 6], [1, 6], [3, 4]]) m.box(x, 12, -2, x + 1, 12 + h, 1, c);
  }
  m.box(5, 6, -2, 6, 10, 1, c); m.box(7, 9, -2, 8, 12, 1, c); // thumb
  if (glow) { m.glow(1); for (const [x, y] of [[-8, 13], [9, 16], [-1, 21], [-7, 6]]) { m.set(x, y, 0, 0xfff2a8); m.set(x + 1, y, 0, 0xfff2a8); m.set(x, y + 1, 0, 0xfff2a8); } m.glow(0); }
}

export const ITEM_MODELS = {
  // "Übernehmen": a pointing hand with a little crown spark (take over a unit)
  hand() {
    const m = new VoxModel(0.08);
    hand(m, { fingers: 1, c: SKIN, cd: SKIN_D, cuff: 0x3d6fd8 });
    m.glow(1); for (const [x, y] of [[-4, 19], [3, 19], [-5, 15], [4, 15]]) m.set(x, y, -1, 0xffd23a); m.glow(0); // little sparks
    return m;
  },
  // Gotteshand: a big open white glove, a few sparkles
  grab() {
    const m = new VoxModel(0.08);
    hand(m, { fingers: 4, c: GLOVE, cd: GLOVE_D, cuff: 0xffc93c, glow: true });
    return m;
  },
  // pineapple grenade: ribbed green egg, grey lever, yellow pin ring
  granate() {
    const m = new VoxModel(0.08);
    m.sphere(0, 5, 0, 4.2, (x, y, z) => (y % 3 === 0 || (x + z) % 3 === 0) ? 0x3f5b2a : 0x5d7f3a, 1.2);
    cyl(m, 0, 0, 1.6, 10, 11, 0x8d97a3);
    m.box(1, 10, -1, 4, 11, 0, 0x6c7580); m.box(4, 5, -1, 4, 10, 0, 0x6c7580); // lever
    ring(m, -2, 11, 0, 1.2, 2, 0xffc93c); // pin ring
    return m;
  },
  // explosive barrel: red drum, yellow bands, warning flash
  fass() {
    const m = new VoxModel(0.08);
    cyl(m, 0, 0, 4.5, 0, 12, (x, y) => (y === 2 || y === 10) ? 0xffc93c : (y === 0 || y === 12) ? 0x7a1e1e : 0xc8302a);
    cyl(m, 1, 1, 1, 13, 13, 0x5a5f66); // bung
    m.box(-1, 5, 4, 0, 7, 5, 0x111111); m.set(-1, 6, 5, 0xffc93c); m.set(0, 6, 5, 0xffc93c); // flash on the front
    return m;
  },
  // meteor: craggy rock with a glowing hot side and a short trail
  meteor() {
    const m = new VoxModel(0.08);
    m.sphere(0, 6, 0, 4.5, (x, y, z) => hash(x, y, z) < 0.18 ? 0x3c3530 : x + y * 0.3 > 4 ? 0xff7a1a : 0x6b5d52);
    m.glow(1);
    m.sphere(3, 8, 0, 2.3, (x, y, z) => hash(x, y, z) < 0.5 ? 0xffb03a : 0xff5a1a);
    for (let i = 0; i < 5; i++) m.box(4 + i, 9 + i, -1 + (i & 1), 5 + i, 10 + i, 0 + (i & 1), i < 2 ? 0xffd23a : 0xff7a1a);
    m.glow(0);
    return m;
  },
  // catapult: wooden frame, the arm with the stone in its cup
  katapult() {
    const m = new VoxModel(0.08);
    const W = 0x8a5a2b, WD = 0x5e3b1a;
    m.box(-7, 1, -4, 6, 1, -3, W); m.box(-7, 1, 3, 6, 1, 4, W); // rails
    m.box(-7, 1, -2, -6, 1, 2, WD); m.box(5, 1, -2, 6, 1, 2, WD);
    for (const z of [-4, 4]) { m.set(-6, 0, z, WD); m.set(5, 0, z, WD); } // wheels
    m.box(-1, 2, -4, 0, 7, -4, W); m.box(-1, 2, 4, 0, 7, 4, W); m.box(-1, 7, -3, 0, 7, 3, WD); // A-frame + axle
    for (let i = 0; i < 9; i++) m.set(-1 + Math.round(i * 0.75), 3 + i, 0, W); // arm (leaning)
    m.box(4, 11, -1, 7, 11, 1, WD); // cup
    m.sphere(5, 14, 0, 2.4, (x, y, z) => hash(x, y, z) < 0.3 ? 0x6e7074 : 0x9a9ca0);
    return m;
  },
  // artillery shell: brass case, olive body, red band, pointed tip
  artillerie() {
    const m = new VoxModel(0.07);
    cyl(m, 0, 0, 3, 0, 5, (x, y) => y === 0 ? 0xa7781f : 0xd8a93a);
    cyl(m, 0, 0, 3, 6, 11, (x, y) => y === 7 ? 0xc8302a : 0x55603a);
    cyl(m, 0, 0, 2.3, 12, 13, 0x55603a); cyl(m, 0, 0, 1.5, 14, 15, 0x6b7448); cyl(m, 0, 0, 0.6, 16, 17, 0x8d97a3);
    return m;
  },
  // stink cloud: green puff over a little gas canister, stink lines
  stinkwolke() {
    const m = new VoxModel(0.08);
    cyl(m, 0, 0, 1.8, 0, 5, (x, y) => y === 3 ? 0xffc93c : 0x5d6b2a); m.box(0, 6, 0, 0, 6, 0, 0x8d97a3);
    const G = (x, y, z) => hash(x, y, z) < 0.22 ? 0x6fae2e : 0x9be04a;
    m.sphere(0, 11, 0, 3.6, G); m.sphere(-4, 10, 0, 2.6, G); m.sphere(4, 10, 1, 2.8, G); m.sphere(1, 14, -1, 2.6, G);
    for (const [x, z] of [[-6, -1], [6, 0], [0, 4]]) { m.set(x, 15, z, 0x4e7a1f); m.set(x + 1, 16, z, 0x4e7a1f); m.set(x, 17, z, 0x4e7a1f); }
    m.set(-1, 12, 3, 0x1a1a1a); m.set(1, 12, 3, 0x1a1a1a); m.box(-1, 10, 3, 1, 10, 3, 0x1a1a1a); // grumpy face
    return m;
  },
  // atom bomb (as on the crate screen)
  atom() {
    const m = new VoxModel(0.08);
    m.sphere(0, 6, 0, 4.2, (x, y) => y === 6 || y === 7 ? 0xffd23a : 0x3d4a2f, 1.25);
    m.box(-1, 11, -1, 0, 13, 0, 0x22252b); m.box(-4, 0, -1, 3, 1, 0, 0x22252b); m.box(-1, 0, -4, 0, 1, 3, 0x22252b);
    m.glow(1); m.box(-1, 14, -1, 0, 14, 0, 0xff8a2a); m.glow(0);
    return m;
  },
  // orbital laser satellite (as on the crate screen)
  orbitallaser() {
    const m = new VoxModel(0.08);
    m.box(-2, 4, -2, 1, 8, 1, 0xd9dde4); m.box(-9, 6, -1, -3, 6, 0, 0x2f4fbf); m.box(2, 6, -1, 8, 6, 0, 0x2f4fbf);
    m.box(-1, 9, -1, 0, 10, 0, 0x8d97a3);
    m.glow(1); m.box(-1, 0, -1, 0, 3, 0, 0xff3b30); m.set(-1, 4, -2, 0x8ff8ff); m.glow(0);
    return m;
  },
  // tornado: a funnel of rings that widen and drift upwards, a bit of debris
  disaster_tornado() {
    const m = new VoxModel(0.07);
    for (let y = 0; y <= 18; y++) {
      const r = 0.8 + y * 0.32, ox = Math.round(Math.sin(y * 0.45) * 1.5), oz = Math.round(Math.cos(y * 0.3));
      ring(m, ox, y, oz, Math.max(0, r - 1.1), r, (x, yy, z) => (((Math.atan2(z - oz, x - ox) * 3 + yy * 0.8) % 2) + 2) % 2 < 1 ? 0xb9ad98 : 0x8f8473);
    }
    m.box(6, 10, 0, 6, 10, 0, 0x8a5a2b); m.box(-7, 14, 1, -7, 14, 1, 0x6b5d52); m.box(4, 16, -5, 4, 16, -5, 0x8a5a2b);
    return m;
  },
  // meteor shower: three glowing rocks falling diagonally
  disaster_meteore() {
    const m = new VoxModel(0.07);
    const rock = (cx, cy, cz, r) => {
      m.sphere(cx, cy, cz, r, (x, y, z) => hash(x, y, z) < 0.3 ? 0x3c3530 : 0x6b5d52);
      m.glow(1);
      for (let i = 1; i <= 4; i++) m.sphere(cx + i * 1.4 | 0, cy + i * 1.6 | 0, cz, Math.max(0.6, r - i * 0.45), i < 2 ? 0xffd23a : 0xff6a1a);
      m.glow(0);
    };
    rock(-5, 3, 0, 2.2); rock(1, 9, -2, 1.8); rock(5, 2, 3, 1.5);
    return m;
  },
  // black hole: a black core with a glowing accretion disk
  disaster_loch() {
    const m = new VoxModel(0.07);
    m.sphere(0, 6, 0, 3.6, 0x07070b);
    m.glow(1);
    ring(m, 0, 6, 0, 4.4, 8.5, (x, y, z) => { const d = Math.hypot(x, z); return d < 5.6 ? 0xffe08a : d < 7 ? 0xff8a2a : 0x8a3ab8; });
    ring(m, 0, 5, 0, 5, 6.5, 0xff6a1a); ring(m, 0, 7, 0, 5, 6.5, 0xff6a1a);
    m.glow(0);
    return m;
  },
  // lava flood: a glowing blob with a dark crust and bubbles
  disaster_lava() {
    const m = new VoxModel(0.08);
    m.glow(1);
    m.sphere(0, 2, 0, 5.5, (x, y, z) => hash(x, y, z) < 0.5 ? 0xff5a1a : 0xffa02a, 0.5);
    m.sphere(-3, 2, 4, 2.8, 0xff7a1a, 0.6);
    m.set(1, 6, 0, 0xffd23a); m.set(-2, 5, 2, 0xffd23a); m.set(2, 7, -1, 0xffe08a);
    m.glow(0);
    for (let i = 0; i < 26; i++) { const a = i * 2.4, r = 2 + (i % 4); m.set(Math.round(Math.cos(a) * r), 4 - (r > 4 ? 1 : 0), Math.round(Math.sin(a) * r), 0x3a2a24); }
    m.box(-7, 0, -1, -6, 1, 0, 0x4a4040); m.box(5, 0, 3, 6, 1, 4, 0x4a4040); // cooled stone
    return m;
  },
  // boss crate: dark crate, red bands, a glowing skull on the front
  boss() {
    const m = new VoxModel(0.08);
    const edge = (x, y, z) => [x === -5 || x === 5, y === 0 || y === 9, z === -5 || z === 5].filter(Boolean).length >= 2;
    m.box(-5, 0, -5, 5, 9, 5, (x, y, z) => edge(x, y, z) ? 0xeb4b4b : (y === 4 || y === 5) ? 0x9e2626 : (hash(x, y, z) < 0.5 ? 0x2e343e : 0x343b46));
    m.glow(1);
    for (const z of [6, -6]) {
      m.box(-2, 3, z, 2, 7, z, 0xfff2e0); m.set(-1, 5, z, 0x220000); m.set(1, 5, z, 0x220000); m.set(0, 4, z, 0x220000);
      m.box(-1, 2, z, 1, 2, z, 0xfff2e0); m.set(0, 2, z, 0x220000);
    }
    m.glow(0);
    return m;
  },
};

/** menu model for a tool key (unknown keys: a small question block) */
export function itemObject(key) {
  const f = ITEM_MODELS[key];
  if (f) return voxObject(f());
  const m = new VoxModel(0.08); m.box(-3, 0, -3, 3, 6, 3, 0x8d97a3); return voxObject(m);
}
